import { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * CurrencyMismatchWarning - Floating centered modal for currency selection
 */
const CurrencyMismatchWarning = ({ 
  displayCurrency, 
  displayAmount,
  paymentCurrency, 
  paymentAmount,
  onChooseDisplay,
  onChoosePayment,
  gatewayName,
  isOpen = true,
  onClose
}) => {
  const [show, setShow] = useState(isOpen);

  useEffect(() => {
    setShow(isOpen && displayCurrency !== paymentCurrency);
  }, [isOpen, displayCurrency, paymentCurrency]);

  if (!show || displayCurrency === paymentCurrency) return null;

  const handleChooseDisplay = () => {
    onChooseDisplay?.();
    setShow(false);
    onClose?.();
  };

  const handleChoosePayment = () => {
    onChoosePayment?.();
    setShow(false);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60"
            onClick={handleChoosePayment}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={handleChoosePayment}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Content */}
            <div className="p-6 pt-8">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-7 w-7 text-amber-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
                Which currency do you want to pay in?
              </h3>
              
              {/* Subtitle */}
              <p className="text-sm text-gray-500 text-center mb-6">
                You're viewing prices in {displayCurrency}, but {gatewayName} charges in {paymentCurrency}.
              </p>

              {/* Options */}
              <div className="space-y-3">
                {/* Pay in Display Currency */}
                <button
                  onClick={handleChooseDisplay}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-black hover:bg-gray-50 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Pay in {displayCurrency}</p>
                      <p className="text-2xl font-bold text-gray-900">{displayAmount}</p>
                    </div>
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full group-hover:border-black transition-colors" />
                  </div>
                </button>

                {/* Pay in Payment Currency */}
                <button
                  onClick={handleChoosePayment}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-black hover:bg-gray-50 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Pay in {paymentCurrency}</p>
                      <p className="text-2xl font-bold text-gray-900">{paymentAmount}</p>
                    </div>
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full group-hover:border-black transition-colors" />
                  </div>
                </button>
              </div>

              {/* Note */}
              <p className="text-xs text-gray-400 text-center mt-5">
                Exchange rates may vary at time of payment
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CurrencyMismatchWarning;
